import React, { ReactElement, CSSProperties } from 'react';

import styled from 'styled-components';

import Typography from '@components/Typography';
import CustomIcon from '@components/utils/CustomIcon';
import arrowRight from '@assets/icons/icon-arrow-right.svg';
import arrowDown from '@assets/icons/icon-arrow-down.svg';
import { useDevice } from '@contexts/device';
import { ProviderType } from '@api/v3';

export enum ArrowAlign {
  NO_ARROW = '0',
  UP_DOWN = '1',
  FIXED_RIGHT = '2',
}

interface ShowMoreCardProps {
  title: string;
  navigationText: string | ReactElement;
  onNavigate?: () => void;
  isShowMore?: boolean;
  arrowAlign?: ArrowAlign;
  containerStyle?: CSSProperties;
  isShowAll?: boolean;
  provider?: ProviderType;
}

const Container = styled.div`
  width: 100%;
  padding: 40px 40px 20px 40px;
  border-radius: 0 0 8px 8px;
  color: ${({ theme }) => theme.palette.darkGrey};
  box-shadow: 0 10px 20px 0 rgba(58, 58, 58, 0.1);
  position: relative;
  background-color: ${({ theme }) => theme.palette.white};
  white-space: nowrap;
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.TABLET})`} {
    margin: 20px 0;
    padding: 23px 5px 21px 5px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.DESKTOP})`} {
    padding-top: 20px;
  }
`;

const Title = styled.h2`
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.DESKTOP})`} {
    margin-left: 25px;
  }
`;

const NavigationText = styled.button`
  display: flex;
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

const Navigation = styled.div`
  z-index: 10;
  display: flex;
  justify-content: flex-end;
  margin-top: -25px;
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.TABLET})`} {
    width: 100%;
    margin: 21px 0 0 -10px;
  }
`;

const Children = styled.div`
  width: 100%;
  margin-top: 40px;
  white-space: nowrap;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.PHONE})`} {
    margin-top: 10px;
  }
`;

const Arrow = styled.div`
  margin-left: 5px;
`;

const ShowMoreCard = ({
  title,
  navigationText,
  onNavigate,
  children,
  isShowMore = true,
  arrowAlign = ArrowAlign.NO_ARROW,
  containerStyle,
  isShowAll,
  provider,
}: React.PropsWithChildren<ShowMoreCardProps>) => {
  const { isMobile } = useDevice();

  return (
    <Container {...(containerStyle ? { style: containerStyle } : {})}>
      <Wrapper>
        <Title>
          <Typography
            variant={isMobile ? 'caption1' : 'Title4'}
            color='D400'
            weight='Bold'
          >
            {title}
          </Typography>
        </Title>
        {isMobile && onNavigate ? (
          <Navigation>
            {provider === ProviderType.SETUR_LOCAL_HOTEL ? (
              <NavigationText onClick={onNavigate}>
                <Typography variant='caption2' color='BR300' weight='Bold'>
                  {navigationText}
                </Typography>
                {arrowAlign === ArrowAlign.NO_ARROW ? null : (
                  <Arrow>
                    <CustomIcon
                      isOpen={ArrowAlign.UP_DOWN && isShowMore}
                      icon={
                        arrowAlign === ArrowAlign.UP_DOWN
                          ? arrowDown
                          : arrowRight
                      }
                      fontSize={20}
                    />
                  </Arrow>
                )}
              </NavigationText>
            ) : null}
          </Navigation>
        ) : null}
      </Wrapper>
      {isShowAll ? (
        <>
          {!isMobile && onNavigate ? (
            <Navigation>
              {provider === ProviderType.SETUR_LOCAL_HOTEL ? (
                <NavigationText onClick={onNavigate}>
                  <Typography variant='caption2' color='BR300' weight='Bold'>
                    {navigationText}
                  </Typography>
                  {arrowAlign === ArrowAlign.NO_ARROW ? null : (
                    <Arrow>
                      <CustomIcon
                        isOpen={ArrowAlign.UP_DOWN && isShowMore}
                        icon={
                          arrowAlign === ArrowAlign.UP_DOWN
                            ? arrowDown
                            : arrowRight
                        }
                        fontSize={20}
                      />
                    </Arrow>
                  )}
                </NavigationText>
              ) : null}
            </Navigation>
          ) : null}
          <Children>{children}</Children>
        </>
      ) : (
        <>
          <Children>{children}</Children>
          {!isMobile && onNavigate ? (
            <Navigation>
              {provider === ProviderType.SETUR_LOCAL_HOTEL ? (
                <NavigationText onClick={onNavigate}>
                  <Typography variant='caption2' color='BR300' weight='Bold'>
                    {navigationText}
                  </Typography>
                  {arrowAlign === ArrowAlign.NO_ARROW ? null : (
                    <Arrow>
                      <CustomIcon
                        isOpen={ArrowAlign.UP_DOWN && isShowMore}
                        icon={
                          arrowAlign === ArrowAlign.UP_DOWN
                            ? arrowDown
                            : arrowRight
                        }
                        fontSize={20}
                      />
                    </Arrow>
                  )}
                </NavigationText>
              ) : null}
            </Navigation>
          ) : null}
        </>
      )}
    </Container>
  );
};

export default ShowMoreCard;
